/**
 * Floating task controls — stacked above the 🐞 Report button (which sits at bottom:20). Two pills:
 *   📋 Delegate (bottom:72) — captures the current screen, opens the delegate modal with {kind:'screen'}.
 *   🗂 Tasks   (bottom:124) — opens the in-app TaskPanel.
 * Internal staff only (App gates rendering). Visible-but-disabled with an explanation while the task API
 * isn't write-enabled, rather than hidden — same idiom as the /tasks banner.
 */
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { captureViewport } from '../lib/screenshot';
import { currentViewTab, currentPageUrl, friendlyRouteLabel } from '../lib/captureContext';
import { useTaskConfigured, TASK_DISABLED_TITLE } from '../lib/useTaskConfigured';
import type { TaskContext } from '../lib/api';
import DelegateTaskModal from './DelegateTaskModal';

const PILL: React.CSSProperties = {
  position: 'fixed', right: 20, zIndex: 900, fontSize: 13, fontWeight: 600, padding: '10px 16px',
  borderRadius: 999, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.22)',
};

export default function TaskFab({ onOpenPanel }: { onOpenPanel: () => void }) {
  const loc = useLocation();
  const { configured } = useTaskConfigured();
  const [capturing, setCapturing] = useState(false);
  const [open, setOpen] = useState(false);
  const [shot, setShot] = useState<string | null>(null);

  const openDelegate = async () => {
    if (!configured) return;
    setCapturing(true);
    const s = await captureViewport(); // capture BEFORE the modal opens so it isn't in the shot
    setShot(s);
    setCapturing(false);
    setOpen(true);
  };

  const context: TaskContext = { kind: 'screen', label: friendlyRouteLabel(loc), view_tab: currentViewTab(loc) ?? undefined };

  return (
    <>
      <button type="button" onClick={onOpenPanel} className="fab"
        title="任务面板 / Task board"
        style={{ ...PILL, bottom: 124, background: 'var(--card,#fff)', color: 'var(--ink)', border: '1px solid var(--rule)' }}>
        🗂 <span className="fab-label">任务 / Tasks</span>
      </button>
      <button type="button" onClick={openDelegate} disabled={!configured || capturing} className="fab"
        title={configured ? '在此屏派发任务 / Delegate a task from this screen' : TASK_DISABLED_TITLE}
        style={{
          ...PILL, bottom: 72, background: configured ? 'var(--accent, #8a6d3b)' : 'var(--muted, #9a9a9a)',
          color: '#fff', cursor: configured ? (capturing ? 'progress' : 'pointer') : 'not-allowed', opacity: configured ? 1 : 0.7,
        }}>
        {capturing ? '📸 ' : '📋 '}<span className="fab-label">{capturing ? '截图中… / Capturing…' : '派发 / Delegate'}</span>
      </button>

      <DelegateTaskModal
        open={open}
        onClose={() => setOpen(false)}
        context={context}
        initialScreenshot={shot}
        pageUrl={currentPageUrl(loc)}
        viewTab={currentViewTab(loc)}
      />
    </>
  );
}
